// ABOUTME: Guest mode app shell that renders the dashboard without authentication.
// ABOUTME: Uses localStorage for persistence; prompts sign-up for cloud sync.

import React, { useMemo, useCallback } from "react";
import { Routes, Route } from "react-router-dom";
import { useGuestDashboard } from "../hooks/useGuestDashboard";
import { useUIStore } from "../stores/ui";
import { useDashboardStore } from "../stores/dashboard";
import { reflowForColumnCount } from "../utils/layoutReflow";
import type { DashboardItem } from "../services/dashboardService";
import { guestDashboardService } from "../services/guestDashboardService";
import { backfillLinkTitle } from "../services/linkTitleService";
import { isUnnamed } from "../utils/linkLabel";
import { downloadBookmarksFile, exportableLinkCount } from "../utils/bookmarkExport";
import StartDashboard from "./StartDashboard";
import Header from "./Header";
import AddDashboardSheet from "./AddDashboardSheet";

interface GuestAppProps {
  onSignInClick: () => void;
  onSignUpClick: () => void;
}

function GuestApp({ onSignInClick, onSignUpClick }: GuestAppProps) {
  const openSheet = useDashboardStore((s) => s.openSheet);
  const { columnCount, setColumnCount } = useUIStore();

  const {
    dashboardItems,
    loading,
    addDashboardItem,
    updateDashboardItem,
    deleteDashboardItem,
    updateDashboardLayout,
  } = useGuestDashboard();

  const installedWidgets = useMemo(() => {
    return Object.fromEntries(
      dashboardItems.filter((i) => i.type === "widget").map((i) => [i.widgetName, true])
    );
  }, [dashboardItems]);

  const existingUrls = useMemo(
    () => dashboardItems.filter((i) => i.type === "link" && i.url).map((i) => i.url as string),
    [dashboardItems]
  );
  const exportable = exportableLinkCount(dashboardItems);

  const handleAddLink = useCallback(
    (newLink: { url: string; name?: string; tags?: string[]; bgColor?: string; col?: number }) => {
      const col = typeof newLink.col === "number" ? newLink.col : 0;
      const order = dashboardItems.filter((i) => i.type === "link" && i.col === col).length;

      const id = addDashboardItem({
        type: "link",
        url: newLink.url,
        name: newLink.name || newLink.url,
        tags: newLink.tags || [],
        ...(newLink.bgColor ? { bgColor: newLink.bgColor } : {}),
        col,
        order,
        createdDate: Date.now(),
      });
      if (isUnnamed(newLink)) {
        void backfillLinkTitle(id, newLink.url, {
          getItem: (itemId) => guestDashboardService.getDashboard().find((i) => i.id === itemId),
          update: updateDashboardItem,
        });
      }
    },
    [dashboardItems, addDashboardItem, updateDashboardItem]
  );

  const handleWidgetInstall = useCallback(
    (name: string) => {
      if (dashboardItems.some((i) => i.type === "widget" && i.widgetName === name)) return;

      const colCounts = [0, 1, 2].map((col) => dashboardItems.filter((i) => i.col === col).length);
      const col = colCounts.indexOf(Math.min(...colCounts));
      const order = dashboardItems.filter((i) => i.col === col).length;

      addDashboardItem({
        type: "widget",
        widgetName: name,
        col,
        order,
      });
    },
    [dashboardItems, addDashboardItem]
  );

  const handleWidgetRemove = (name: string) => {
    const widget = dashboardItems.find((i) => i.type === "widget" && i.widgetName === name);
    if (widget) deleteDashboardItem(widget.id);
  };

  const handleDeleteItem = (id: string) => {
    deleteDashboardItem(id);
  };

  const handleEditLink = (id: string, updated: Partial<DashboardItem>) => {
    updateDashboardItem(id, updated);
  };

  const handleChangeColumns = (count: number) => {
    if (count < 2 || count > 5) return;
    setColumnCount(count);

    // See the note in AuthenticatedApp — preserves column identity.
    const newItems = reflowForColumnCount(dashboardItems, count);
    if (newItems !== dashboardItems) {
      updateDashboardLayout(newItems);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] transition-colors">
      {/* Guest mode banner. Honest about where the data lives: guest links
          stay in this browser and are merged into an account only when the
          user signs in *here*, so the banner says so and offers an export. */}
      <div
        role="status"
        className="bg-[var(--color-bg-hover)] border-b border-[var(--color-border)] text-[var(--color-text-secondary)] text-xs py-1.5 px-4"
      >
        <span>Saved in this browser only. </span>
        <button onClick={onSignInClick} className="text-[var(--color-accent)] hover:underline">
          Sign in
        </button>
        <span> or </span>
        <button onClick={onSignUpClick} className="text-[var(--color-accent)] hover:underline">
          create an account
        </button>
        <span> to keep them everywhere</span>
        {exportable > 0 && (
          <>
            <span> · </span>
            <button
              onClick={() => downloadBookmarksFile(dashboardItems)}
              className="text-[var(--color-accent)] hover:underline"
            >
              Export
            </button>
          </>
        )}
      </div>

      <Header
        user={null}
        items={dashboardItems}
        onAddClick={() => openSheet("Links")}
        className="sticky top-0 z-40"
      />

      <main className="mx-auto max-w-7xl p-0 lg:px-8">
        <Routes>
          <Route
            path="*"
            element={
              <StartDashboard
                dashboardItems={dashboardItems}
                setDashboardItems={updateDashboardLayout}
                onWidgetRemove={handleWidgetRemove}
                onDeleteItem={handleDeleteItem}
                onEditLink={handleEditLink}
                loading={loading}
                columnCount={columnCount}
              />
            }
          />
        </Routes>
      </main>

      <AddDashboardSheet
        onAddLink={handleAddLink}
        loading={loading}
        onWidgetInstall={handleWidgetInstall}
        onWidgetRemove={handleWidgetRemove}
        installedWidgets={installedWidgets}
        existingUrls={existingUrls}
        columnCount={columnCount}
        onChangeColumns={handleChangeColumns}
      />
    </div>
  );
}

export default GuestApp;
